import { useEffect, useState, type ReactNode } from "react";
import { Loading } from "@/components/Loading";
import { useConfirm } from "@/components/ConfirmDialog";
import { Empty } from "@/components/Empty";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectEmpty,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ApiError } from "@/lib/apiClient";
import { FIELD_MAX } from "@/lib/fieldLimits";
import { toApiDateTime, dateOnly } from "@/utils/dates";
import { merchantSubresources as api, type MerchantFeeScheduleRow, type MerchantPrincipalRow, type MerchantTerminalRow, type MerchantUserRow, type SaveMerchantFeeScheduleRequest, type SaveMerchantPrincipalRequest, type SaveMerchantTerminalRequest, type SaveMerchantUserRequest } from "@/features/merchants/api/merchants.api";
import { products as productsApi, type ProductListItem } from "@/features/catalog/api/catalog.api";

const CUSTOM_PRODUCT = "__custom__";

export function MerchantSubresourceSections({ merchantId }: { merchantId: string }) {
  return (
    <>
      <TerminalsSection merchantId={merchantId} />
      <PrincipalsSection merchantId={merchantId} />
      <FeeScheduleSection merchantId={merchantId} />
      <MerchantUsersSection merchantId={merchantId} />
    </>
  );
}

interface TerminalDraft {
  id: string | null;
  productId: string | null;
  model: string;
  serialNumber: string;
  status: string;
  notes: string;
  saving: boolean;
}

function TerminalsSection({ merchantId }: { merchantId: string }) {
  const confirm = useConfirm();
  const list = useResourceList(
    () => api.terminals.list(merchantId),
    [merchantId],
  );

  const [drafts, setDrafts] = useState<TerminalDraft[]>([]);

  const [catalog, setCatalog] = useState<ProductListItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    productsApi.listAll()
      .then((rows) => { if (!cancelled) setCatalog(rows.filter((p) => p.type === "hardware")); })
      .catch(() => {  });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (list.kind === "loaded") setDrafts(list.rows.map(toTerminalDraft));
  }, [list.kind === "loaded" ? list.rows : null]);

  function setDraft(idx: number, patch: Partial<TerminalDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  function pickProduct(idx: number, productId: string) {
    const d = drafts[idx];
    const p = productId ? catalog.find((c) => c.id === productId) : undefined;

    setDraft(idx, {
      productId: p?.id ?? null,
      model: !d.model.trim() && p ? p.name : d.model,
    });
  }

  async function save(idx: number) {
    const d = drafts[idx];
    if (!d.model.trim()) { list.setError("Model is required."); return; }
    setDraft(idx, { saving: true });
    try {
      const body: SaveMerchantTerminalRequest = {
        model: d.model.trim(),
        serialNumber: d.serialNumber.trim() || null,
        status: d.status || "Active",
        notes: d.notes.trim() || null,
        productId: d.productId,
      };
      if (d.id) await api.terminals.update(merchantId, d.id, body);
      else      await api.terminals.create(merchantId, body);
      await list.reload();
    } catch (e) {
      list.setError(e instanceof ApiError ? e.message : "Save failed.");
      setDraft(idx, { saving: false });
    }
  }

  async function remove(idx: number) {
    const d = drafts[idx];
    if (!d.id) { setDrafts((prev) => prev.filter((_, i) => i !== idx)); return; }
    if (!(await confirm({ description: `Remove terminal "${d.model}"?`, confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.terminals.remove(merchantId, d.id);
      await list.reload();
    } catch (e) { list.setError(e instanceof ApiError ? e.message : "Delete failed."); }
  }

  function addBlank() {
    setDrafts((prev) => [...prev, {
      id: null, productId: null, model: "", serialNumber: "", status: "Active", notes: "", saving: false,
    }]);
  }

  return (
    <SubresourceShell
      title={`Terminals (${list.kind === "loaded" ? list.rows.length : "…"})`}
      state={list}
      onAdd={addBlank}
    >
      {drafts.length === 0 ? (
        <Empty block>No terminals on file.</Empty>
      ) : (
        <Table headers={["Catalog product", "Model", "Serial number", "Status", "Notes", ""]}>
          {drafts.map((d, i) => (
            <tr key={d.id ?? `new-${i}`}>
              <Td>
                <SearchableSelect
                  value={d.productId ?? CUSTOM_PRODUCT}
                  onValueChange={(v) => pickProduct(i, v === CUSTOM_PRODUCT ? "" : v)}
                  className="h-8"
                  contentClassName="w-72"
                  searchPlaceholder="Search hardware…"
                  options={[
                    { value: CUSTOM_PRODUCT, label: "— Custom —" },
                    ...catalog.map((p) => ({
                      value: p.id,
                      label: `${p.name}${p.sku ? ` (${p.sku})` : ""}`,
                    })),
                  ]}
                />
              </Td>
              <Td><TextInput value={d.model} onChange={(v) => setDraft(i, { model: v })} maxLength={FIELD_MAX.merchantTerminal.model} /></Td>
              <Td><TextInput value={d.serialNumber} onChange={(v) => setDraft(i, { serialNumber: v })} maxLength={FIELD_MAX.merchantTerminal.serialNumber} /></Td>
              <Td>
                <SelectInput
                  value={d.status}
                  onChange={(v) => setDraft(i, { status: v })}
                  options={["Active", "Returned", "Damaged", "Lost"]}
                />
              </Td>
              <Td><TextInput value={d.notes} onChange={(v) => setDraft(i, { notes: v })} maxLength={FIELD_MAX.merchantTerminal.notes} /></Td>
              <Td><RowActions saving={d.saving} isNew={!d.id} onSave={() => save(i)} onDelete={() => remove(i)} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </SubresourceShell>
  );
}

function toTerminalDraft(r: MerchantTerminalRow): TerminalDraft {
  return {
    id: r.id,
    productId: r.productId,
    model: r.model,
    serialNumber: r.serialNumber ?? "",
    status: r.status,
    notes: r.notes ?? "",
    saving: false,
  };
}

interface PrincipalDraft {
  id: string | null;
  firstName: string;
  lastName: string;
  title: string;
  ownershipPct: string;
  email: string;
  phone: string;
  saving: boolean;
}

function PrincipalsSection({ merchantId }: { merchantId: string }) {
  const confirm = useConfirm();
  const list = useResourceList(
    () => api.principals.list(merchantId),
    [merchantId],
  );

  const [drafts, setDrafts] = useState<PrincipalDraft[]>([]);
  useEffect(() => {
    if (list.kind === "loaded") setDrafts(list.rows.map(toPrincipalDraft));
  }, [list.kind === "loaded" ? list.rows : null]);

  function setDraft(idx: number, patch: Partial<PrincipalDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  async function save(idx: number) {
    const d = drafts[idx];
    if (!d.firstName.trim() || !d.lastName.trim()) {
      list.setError("First and last name are required."); return;
    }
    setDraft(idx, { saving: true });
    try {
      const pct = d.ownershipPct.trim();
      const body: SaveMerchantPrincipalRequest = {
        firstName: d.firstName.trim(),
        lastName: d.lastName.trim(),
        title: d.title.trim() || null,

        ownershipPercentage: pct ? Number(pct) / 100 : null,
        email: d.email.trim() || null,
        phone: d.phone.trim() || null,
      };
      if (d.id) await api.principals.update(merchantId, d.id, body);
      else      await api.principals.create(merchantId, body);
      await list.reload();
    } catch (e) {
      list.setError(e instanceof ApiError ? e.message : "Save failed.");
      setDraft(idx, { saving: false });
    }
  }

  async function remove(idx: number) {
    const d = drafts[idx];
    if (!d.id) { setDrafts((prev) => prev.filter((_, i) => i !== idx)); return; }
    if (!(await confirm({ description: `Remove principal "${d.firstName} ${d.lastName}"?`, confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.principals.remove(merchantId, d.id);
      await list.reload();
    } catch (e) { list.setError(e instanceof ApiError ? e.message : "Delete failed."); }
  }

  function addBlank() {
    setDrafts((prev) => [...prev, {
      id: null, firstName: "", lastName: "", title: "",
      ownershipPct: "", email: "", phone: "", saving: false,
    }]);
  }

  return (
    <SubresourceShell
      title={`Principals / owners (${list.kind === "loaded" ? list.rows.length : "…"})`}
      state={list}
      onAdd={addBlank}
    >
      {drafts.length === 0 ? (
        <Empty block>No principals on file.</Empty>
      ) : (
        <Table headers={["First name", "Last name", "Title", "Ownership %", "Email", "Phone", ""]}>
          {drafts.map((d, i) => (
            <tr key={d.id ?? `new-${i}`}>
              <Td><TextInput value={d.firstName} onChange={(v) => setDraft(i, { firstName: v })} /></Td>
              <Td><TextInput value={d.lastName} onChange={(v) => setDraft(i, { lastName: v })} /></Td>
              <Td><TextInput value={d.title} onChange={(v) => setDraft(i, { title: v })} /></Td>
              <Td><NumInput value={d.ownershipPct} max={100} onChange={(v) => setDraft(i, { ownershipPct: v })} /></Td>
              <Td><TextInput value={d.email} onChange={(v) => setDraft(i, { email: v })} /></Td>
              <Td><TextInput value={d.phone} onChange={(v) => setDraft(i, { phone: v })} /></Td>
              <Td><RowActions saving={d.saving} isNew={!d.id} onSave={() => save(i)} onDelete={() => remove(i)} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </SubresourceShell>
  );
}

function toPrincipalDraft(r: MerchantPrincipalRow): PrincipalDraft {
  return {
    id: r.id,
    firstName: r.firstName,
    lastName: r.lastName,
    title: r.title ?? "",
    ownershipPct: r.ownershipPercentage != null ? String(r.ownershipPercentage * 100) : "",
    email: r.email ?? "",
    phone: r.phone ?? "",
    saving: false,
  };
}

interface FeeDraft {
  id: string | null;
  effectiveAt: string;
  notes: string;
  visaMcDiscoverRate: string;
  amexRate: string;
  transactionFee: string;
  authorizationFee: string;
  monthlyFee: string;
  monthlyPciFee: string;
  monthlyMinimumFee: string;
  batchFee: string;
  chargebackFee: string;
  annualFee: string;
  saving: boolean;
}

function FeeScheduleSection({ merchantId }: { merchantId: string }) {
  const confirm = useConfirm();
  const list = useResourceList(
    () => api.feeSchedule.list(merchantId),
    [merchantId],
  );

  const [drafts, setDrafts] = useState<FeeDraft[]>([]);
  useEffect(() => {
    if (list.kind === "loaded") setDrafts(list.rows.map(toFeeDraft));
  }, [list.kind === "loaded" ? list.rows : null]);

  function setDraft(idx: number, patch: Partial<FeeDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  async function save(idx: number) {
    const d = drafts[idx];
    setDraft(idx, { saving: true });
    try {
      const body: SaveMerchantFeeScheduleRequest = {
        effectiveAt: toApiDateTime(d.effectiveAt) ?? undefined,
        notes: d.notes.trim() || null,

        visaMcDiscoverRate: pctToFraction(d.visaMcDiscoverRate),
        americanExpressOptBlueRate: pctToFraction(d.amexRate),
        transactionFee: numOrNull(d.transactionFee),
        authorizationFee: numOrNull(d.authorizationFee),
        monthlyFee: numOrNull(d.monthlyFee),
        monthlyPciFee: numOrNull(d.monthlyPciFee),
        monthlyMinimumFee: numOrNull(d.monthlyMinimumFee),
        batchFee: numOrNull(d.batchFee),
        chargebackFee: numOrNull(d.chargebackFee),
        annualFee: numOrNull(d.annualFee),
      };
      if (d.id) await api.feeSchedule.update(merchantId, d.id, body);
      else      await api.feeSchedule.create(merchantId, body);
      await list.reload();
    } catch (e) {
      list.setError(e instanceof ApiError ? e.message : "Save failed.");
      setDraft(idx, { saving: false });
    }
  }

  async function remove(idx: number) {
    const d = drafts[idx];
    if (!d.id) { setDrafts((prev) => prev.filter((_, i) => i !== idx)); return; }
    if (!(await confirm({ description: "Remove this fee-schedule row?", confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.feeSchedule.remove(merchantId, d.id);
      await list.reload();
    } catch (e) { list.setError(e instanceof ApiError ? e.message : "Delete failed."); }
  }

  function addBlank() {
    setDrafts((prev) => [...prev, {
      id: null,
      effectiveAt: new Date().toISOString().slice(0, 10),
      notes: "",
      visaMcDiscoverRate: "", amexRate: "",
      transactionFee: "", authorizationFee: "",
      monthlyFee: "", monthlyPciFee: "", monthlyMinimumFee: "",
      batchFee: "", chargebackFee: "", annualFee: "",
      saving: false,
    }]);
  }

  return (
    <SubresourceShell
      title={`Fee schedule (${list.kind === "loaded" ? list.rows.length : "…"})`}
      state={list}
      onAdd={addBlank}
      wide
    >
      {drafts.length === 0 ? (
        <Empty block>No fee schedule recorded yet.</Empty>
      ) : (
        <div className="overflow-x-auto">
          <Table headers={[
            "Effective", "Visa/MC %", "Amex %", "Trans $", "Auth $",
            "Monthly $", "PCI $", "Min $", "Batch $", "Chargeback $", "Annual $", "Notes", "",
          ]}>
            {drafts.map((d, i) => (
              <tr key={d.id ?? `new-${i}`}>
                <Td><DateInput value={d.effectiveAt} onChange={(v) => setDraft(i, { effectiveAt: v })} /></Td>
                <Td><NumInput value={d.visaMcDiscoverRate} onChange={(v) => setDraft(i, { visaMcDiscoverRate: v })} /></Td>
                <Td><NumInput value={d.amexRate} onChange={(v) => setDraft(i, { amexRate: v })} /></Td>
                <Td><NumInput value={d.transactionFee} onChange={(v) => setDraft(i, { transactionFee: v })} /></Td>
                <Td><NumInput value={d.authorizationFee} onChange={(v) => setDraft(i, { authorizationFee: v })} /></Td>
                <Td><NumInput value={d.monthlyFee} onChange={(v) => setDraft(i, { monthlyFee: v })} /></Td>
                <Td><NumInput value={d.monthlyPciFee} onChange={(v) => setDraft(i, { monthlyPciFee: v })} /></Td>
                <Td><NumInput value={d.monthlyMinimumFee} onChange={(v) => setDraft(i, { monthlyMinimumFee: v })} /></Td>
                <Td><NumInput value={d.batchFee} onChange={(v) => setDraft(i, { batchFee: v })} /></Td>
                <Td><NumInput value={d.chargebackFee} onChange={(v) => setDraft(i, { chargebackFee: v })} /></Td>
                <Td><NumInput value={d.annualFee} onChange={(v) => setDraft(i, { annualFee: v })} /></Td>
                <Td><TextInput value={d.notes} onChange={(v) => setDraft(i, { notes: v })} /></Td>
                <Td><RowActions saving={d.saving} isNew={!d.id} onSave={() => save(i)} onDelete={() => remove(i)} /></Td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </SubresourceShell>
  );
}

function toFeeDraft(r: MerchantFeeScheduleRow): FeeDraft {
  return {
    id: r.id,
    effectiveAt: dateOnly(r.effectiveAt),
    notes: r.notes ?? "",
    visaMcDiscoverRate: fractionToPctStr(r.visaMcDiscoverRate),
    amexRate: fractionToPctStr(r.americanExpressOptBlueRate),
    transactionFee: numToStr(r.transactionFee),
    authorizationFee: numToStr(r.authorizationFee),
    monthlyFee: numToStr(r.monthlyFee),
    monthlyPciFee: numToStr(r.monthlyPciFee),
    monthlyMinimumFee: numToStr(r.monthlyMinimumFee),
    batchFee: numToStr(r.batchFee),
    chargebackFee: numToStr(r.chargebackFee),
    annualFee: numToStr(r.annualFee),
    saving: false,
  };
}

function pctToFraction(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n / 100 : null;
}

function fractionToPctStr(n: number | null): string {
  if (n == null) return "";

  return String(Number((n * 100).toFixed(6)));
}

function numOrNull(s: string): number | null {
  const trimmed = s.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function numToStr(n: number | null): string {
  return n == null ? "" : String(n);
}

interface MerchantUserDraft {
  id: string | null;
  name: string;
  title: string;
  email: string;
  phone: string;
  merchantRole: string;
  saving: boolean;
}

function MerchantUsersSection({ merchantId }: { merchantId: string }) {
  const confirm = useConfirm();
  const list = useResourceList(
    () => api.users.list(merchantId),
    [merchantId],
  );

  const [drafts, setDrafts] = useState<MerchantUserDraft[]>([]);
  useEffect(() => {
    if (list.kind === "loaded") setDrafts(list.rows.map(toMerchantUserDraft));
  }, [list.kind === "loaded" ? list.rows : null]);

  function setDraft(idx: number, patch: Partial<MerchantUserDraft>) {
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  }

  async function save(idx: number) {
    const d = drafts[idx];
    if (!d.name.trim()) { list.setError("Name is required."); return; }
    setDraft(idx, { saving: true });
    try {
      const body: SaveMerchantUserRequest = {
        name: d.name.trim(),
        title: d.title.trim() || null,
        email: d.email.trim() || null,
        phone: d.phone.trim() || null,
        merchantRole: d.merchantRole || "Staff",
      };
      if (d.id) await api.users.update(merchantId, d.id, body);
      else      await api.users.create(merchantId, body);
      await list.reload();
    } catch (e) {
      list.setError(e instanceof ApiError ? e.message : "Save failed.");
      setDraft(idx, { saving: false });
    }
  }

  async function remove(idx: number) {
    const d = drafts[idx];
    if (!d.id) { setDrafts((prev) => prev.filter((_, i) => i !== idx)); return; }
    if (!(await confirm({ description: `Remove "${d.name}"?`, confirmLabel: "Remove", destructive: true }))) return;
    try {
      await api.users.remove(merchantId, d.id);
      await list.reload();
    } catch (e) { list.setError(e instanceof ApiError ? e.message : "Delete failed."); }
  }

  function addBlank() {
    setDrafts((prev) => [...prev, {
      id: null, name: "", title: "", email: "", phone: "", merchantRole: "Staff", saving: false,
    }]);
  }

  return (
    <SubresourceShell
      title={`Merchant users (${list.kind === "loaded" ? list.rows.length : "…"})`}
      state={list}
      onAdd={addBlank}
    >
      {drafts.length === 0 ? (
        <Empty block>No merchant-side staff on file.</Empty>
      ) : (
        <Table headers={["Name", "Title", "Role", "Email", "Phone", ""]}>
          {drafts.map((d, i) => (
            <tr key={d.id ?? `new-${i}`}>
              <Td><TextInput value={d.name} onChange={(v) => setDraft(i, { name: v })} maxLength={FIELD_MAX.merchantUser.name} /></Td>
              <Td><TextInput value={d.title} onChange={(v) => setDraft(i, { title: v })} maxLength={FIELD_MAX.merchantUser.title} /></Td>
              <Td>
                <SelectInput
                  value={d.merchantRole}
                  onChange={(v) => setDraft(i, { merchantRole: v })}
                  options={["Owner", "Manager", "Staff", "Bookkeeper", "Other"]}
                />
              </Td>
              <Td><TextInput value={d.email} onChange={(v) => setDraft(i, { email: v })} maxLength={FIELD_MAX.merchantUser.email} /></Td>
              <Td><TextInput value={d.phone} onChange={(v) => setDraft(i, { phone: v })} maxLength={FIELD_MAX.merchantUser.phone} /></Td>
              <Td><RowActions saving={d.saving} isNew={!d.id} onSave={() => save(i)} onDelete={() => remove(i)} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </SubresourceShell>
  );
}

function toMerchantUserDraft(r: MerchantUserRow): MerchantUserDraft {
  return {
    id: r.id, name: r.name, title: r.title ?? "",
    email: r.email ?? "", phone: r.phone ?? "",
    merchantRole: r.merchantRole, saving: false,
  };
}

type ListKind = "loading" | "loaded" | "error";

interface ResourceList<T> {
  kind: ListKind;
  rows: T[];

  error: string | null;
  setError: (msg: string | null) => void;
  reload: () => Promise<void>;
}

function useResourceList<T>(load: () => Promise<T[]>, deps: unknown[]): ResourceList<T> {
  const [kind, setKind] = useState<ListKind>("loading");
  const [rows, setRows] = useState<T[]>([]);
  const [error, setError] = useState<string | null>(null);

  function classify(e: unknown): string {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      return "You don't have access to this merchant's sub-records.";
    }
    return e instanceof Error ? e.message : "Failed to load.";
  }

  async function reload() {
    setKind("loading");
    try {
      const fresh = await load();
      setRows(fresh);
      setKind("loaded");
      setError(null);
    } catch (e) {
      setError(classify(e));
      setKind("error");
    }
  }

  useEffect(() => {
    let cancelled = false;
    setKind("loading");
    load()
      .then((fresh) => {
        if (cancelled) return;
        setRows(fresh);
        setKind("loaded");
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(classify(e));
        setKind("error");
      });
    return () => { cancelled = true; };

  }, deps);

  return { kind, rows, error, setError, reload };
}

function SubresourceShell<T>({
  title, state, onAdd, children, wide = false,
}: {
  title: string;
  state: ResourceList<T>;
  onAdd: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <section className={`bg-white border border-slate-200 rounded-md shadow-sm ${wide ? "md:col-span-2" : ""}`}>
      <header className="px-4 py-3 border-b border-slate-200 rounded-t-md flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <Button type="button" size="sm" className="h-7 px-2.5" onClick={onAdd}>
          Add
        </Button>
      </header>
      <div className="p-4 space-y-3">
        {state.error && <Notice tone="error">{state.error}</Notice>}
        {state.kind === "loading" && <Loading className="text-xs">Loading…</Loading>}
        {state.kind === "loaded" && children}
      </div>
    </section>
  );
}

function Table({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-600 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children }: { children: ReactNode }) {
  return <td className="px-2 py-1.5 align-top">{children}</td>;
}

function TextInput({ value, onChange, maxLength }: { value: string; onChange: (v: string) => void; maxLength?: number }) {
  return <Input type="text" value={value} onChange={(e) => onChange(e.target.value)} maxLength={maxLength} className="h-8" />;
}

function NumInput({ value, onChange, max }: { value: string; onChange: (v: string) => void; max?: number }) {
  return (
    <Input
      type="number"
      step="any"
      min={0}
      max={max}
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        if (next !== "") {
          const n = Number(next);
          if (n < 0 || (max !== undefined && n > max)) return;
        }
        onChange(next);
      }}
      className="h-8 text-right tabular-nums"
    />
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="h-8" />;
}

function SelectInput({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: string[];
}) {
  return (
    <UISelect value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 ? (
          <SelectEmpty />
        ) : (
          options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)
        )}
      </SelectContent>
    </UISelect>
  );
}

function RowActions({ saving, isNew, onSave, onDelete }: {
  saving: boolean; isNew: boolean; onSave: () => void; onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <Button type="button" size="sm" variant="outline" className="h-7" disabled={saving} onClick={onSave}>
        {saving ? "…" : "Save"}
      </Button>
      <Button type="button" size="sm" variant="link" className="h-auto p-0 text-destructive" onClick={onDelete}>
        {isNew ? "Discard" : "Delete"}
      </Button>
    </div>
  );
}

